import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Star, Package, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import VehicleBanner from '../components/VehicleBanner';
import BrandTrustScore from '../components/BrandTrustScore';
import SortSelect, { sortProducts, useTopRatedFilter } from '../components/SortSelect';
import { brandsAPI, productsAPI, cartAPI, uploadsAPI } from '../services/api';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { usePageMeta, useJsonLd } from '../services/seo';
import { renderMarkdown } from '../services/markdown';
import { useT } from '../services/i18n';
import { catName } from '../services/catalogI18n';
import FaqSection from '../components/FaqSection';
import Breadcrumb from '../components/Breadcrumb';
import TagLinks from '../components/TagLinks';

const BrandPage = () => {
  const { t, lang } = useT();
  const { name } = useParams();
  const navigate = useNavigate();
  const param = decodeURIComponent(name || '');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('categorie') || '';
  const vehicle = useSelectedVehicle();
  const [rawBrand, setBrand] = useState(null);
  // Brand page SEO (title, meta, H1, intro, guide, FAQ) in the UI language when the AI translation exists, FR otherwise
  const brand = useMemo(() => {
    if (!rawBrand) return rawBrand;
    const tr = lang !== 'fr' ? rawBrand.seo_i18n?.[lang] : null;
    return tr ? { ...rawBrand, ...Object.fromEntries(Object.entries(tr).filter(([k, v]) => v && (k !== 'faq' || (Array.isArray(v) && v.length)))) } : rawBrand;
  }, [rawBrand, lang]);
  const [notFound, setNotFound] = useState(false);
  const [products, setProducts] = useState([]);
  const [compatibleIds, setCompatibleIds] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState('relevance');
  const [isLoading, setIsLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);

  useEffect(() => {
    setBrand(null);
    setNotFound(false);
    brandsAPI.getOne(param).then(b => {
      setBrand(b);
      if (b.slug && param !== b.slug) navigate(`/marque/${encodeURIComponent(b.slug)}${window.location.search}`, { replace: true });
    }).catch(() => setNotFound(true));
  }, [param, navigate]);

  const brandName = brand?.name || param.toUpperCase();

  useEffect(() => {
    if (!brand) return undefined;
    let cancelled = false;
    setIsLoading(true);
    const params = vehicleStore.toParams(vehicle);
    Promise.all([
      productsAPI.getAll(null, null, null, brand.name),
      params ? productsAPI.getAll(null, null, params, brand.name) : Promise.resolve(null),
    ]).then(([all, compatible]) => {
      if (cancelled) return;
      setProducts(all);
      setCompatibleIds(compatible ? new Set(compatible.map(p => p.id)) : null);
    }).catch(console.error).finally(() => !cancelled && setIsLoading(false));
    return () => { cancelled = true; };
  }, [brand, vehicle]);

  const handleAddToCart = async (productId) => {
    setAddingToCart(productId);
    try {
      await cartAPI.addItem(productId);
    } finally {
      setAddingToCart(null);
    }
  };

  const selectCategory = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set('categorie', slug); else next.delete('categorie');
    setSearchParams(next, { replace: true });
  };

  const activeCatName = brand?.categories.find(c => c.slug === activeCategory)?.name;
  const canonicalSlug = brand?.slug || brandName;
  const plainDesc = (brand?.description || '').replace(/[#*_>\-]/g, ' ').replace(/\s+/g, ' ').trim();
  usePageMeta(notFound ? { title: t('brand.notFound'), noindex: true } : {
    rawTitle: activeCatName ? undefined : (brand?.seo_title || undefined),
    title: activeCatName ? t('brand.metaCat', { c: activeCatName, b: brandName }) : (brand?.seo_title ? undefined : t('brand.metaTitle', { b: brandName })),
    description: brand?.meta_description
      || brand?.excerpt
      || (plainDesc ? plainDesc.slice(0, 160) : t('brand.metaDesc', { b: brandName, n: brand ? brand.product_count : '', cats: brand ? brand.categories.map(c => c.name.toLowerCase()).slice(0, 4).join(', ') : '' })),
    keywords: brand ? [...(brand.tags || []), ...(brand.secondary_keywords || [])] : [],
    image: brand?.logo,
    path: `/marque/${encodeURIComponent(canonicalSlug)}${activeCategory ? `?categorie=${activeCategory}` : ''}`,
  });
  useJsonLd('brand', brand ? {
    '@context': 'https://schema.org',
    '@type': 'Brand',
    name: brand.name,
    ...(brand.logo ? { logo: brand.logo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${brand.logo}` : brand.logo } : {}),
    ...(brand.description ? { description: brand.description } : {}),
    url: window.location.origin + `/marque/${encodeURIComponent(brand.name)}`,
    ...(brand.total_reviews > 0 ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: Number(brand.avg_rating).toFixed(1), bestRating: '5', worstRating: '1', reviewCount: brand.total_reviews } } : {}),
  } : null);

  const byCategory = activeCategory ? products.filter(p => p.category === activeCategory) : products;
  const topRatedFilter = useTopRatedFilter(compatibleIds && !showAll ? byCategory.filter(p => compatibleIds.has(p.id)) : byCategory);

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="brand-not-found">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('brand.notFound')}</h1>
          <p className="text-gray-500 mb-4">{t('brand.noProducts', { b: brandName })}</p>
          <Link to="/" className="text-blue-600 hover:underline">{t('product.backHome')}</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const visible = sortProducts(topRatedFilter.list, sort);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8" data-testid="brand-page">
        <Breadcrumb
          testId="brand-breadcrumb"
          items={[
            { label: t('header.brands'), to: '/marques' },
            activeCatName ? { label: brandName, to: `/marque/${encodeURIComponent(canonicalSlug)}` } : { label: brandName },
            activeCatName ? { label: activeCatName } : null,
          ]}
        />

        <section className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-6" data-testid="brand-header">
          {!brand ? (
            <div className="flex items-center gap-3 text-gray-400"><Loader2 className="animate-spin text-orange-500" /> Chargement…</div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-6 sm:items-start">
              <div className="w-40 h-24 sm:w-52 sm:h-28 flex items-center justify-center bg-gray-50 border border-gray-100 rounded-lg flex-shrink-0 px-4">
                {brand.logo ? (
                  <img src={uploadsAPI.resolveUrl(brand.logo)} alt={brand.name} className="max-h-full max-w-full object-contain no-placeholder" data-testid="brand-header-logo" />
                ) : (
                  <span className="text-2xl font-black text-gray-700 tracking-wide" data-testid="brand-header-fallback">{brand.name}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold flex items-center gap-1"><Tag size={12} /> {t('brand.eyebrow')}</p>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1" data-testid="brand-title">{brand.h1 || t('search.parts', { b: brand.name })}</h1>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1" data-testid="brand-product-count"><Package size={15} className="text-gray-400" /> {t('brand.products', { n: brand.product_count, count: brand.product_count })}</span>
                  {brand.total_reviews > 0 && (
                    <a href="#confiance" className="flex items-center gap-1 hover:text-orange-600" data-testid="brand-rating"><Star size={15} className="fill-yellow-400 text-yellow-400" /> {brand.avg_rating.toFixed(1)} / 5 · {t('product.reviews', { n: brand.total_reviews, count: brand.total_reviews })}</a>
                  )}
                  <span data-testid="brand-category-count">{t('brand.categories', { n: brand.categories.length, count: brand.categories.length })}</span>
                </div>
                <p className="text-gray-700 mt-4 leading-relaxed" data-testid="brand-excerpt">
                  {brand.excerpt || (!brand.description ? `Retrouvez toutes les pièces détachées ${brand.name} disponibles sur OTOPIECES.COM : qualité d'origine, garantie 2 ans minimum et livraison rapide partout en France.` : '')}
                </p>
              </div>
            </div>
          )}

          {brand && brand.total_reviews > 0 && <BrandTrustScore brand={brand} />}

          {brand && brand.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6 pt-5 border-t border-gray-100" data-testid="brand-categories">
              <button
                type="button"
                data-testid="brand-category-all"
                onClick={() => selectCategory('')}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${!activeCategory ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`}
              >
                {t('tw.allF')} <span className={!activeCategory ? 'text-orange-100' : 'text-gray-400'}>({brand.product_count})</span>
              </button>
              {brand.categories.map(c => (
                <button
                  key={c.slug}
                  type="button"
                  data-testid={`brand-category-${c.slug}`}
                  onClick={() => selectCategory(c.slug)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${activeCategory === c.slug ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`}
                >
                  {catName(c, lang)} <span className={activeCategory === c.slug ? 'text-orange-100' : 'text-gray-400'}>({c.count})</span>
                </button>
              ))}
            </div>
          )}
        </section>

        <VehicleBanner
          vehicle={vehicle}
          count={compatibleIds ? byCategory.filter(p => compatibleIds.has(p.id)).length : undefined}
          showAll={showAll}
          onToggleShowAll={() => setShowAll(v => !v)}
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center" data-testid="brand-empty">
            <p className="text-gray-500">{t('brand.empty', { b: brandName, cat: activeCategory ? t('brand.emptyCat') : '', veh: compatibleIds && !showAll ? t('brand.emptyVeh') : '' })}</p>
            {compatibleIds && !showAll && byCategory.length > 0 && (
              <button onClick={() => setShowAll(true)} className="mt-4 text-blue-600 hover:underline text-sm">{t('brand.seeUnfiltered', { n: byCategory.length })}</button>
            )}
          </div>
        ) : (
          <>
            <SortSelect value={sort} onChange={setSort} count={visible.length} topRated={topRatedFilter.topRated} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="brand-products-grid">
              {visible.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  compatible={compatibleIds ? compatibleIds.has(product.id) : false}
                  onAddToCart={handleAddToCart}
                  isAdding={addingToCart === product.id}
                />
              ))}
            </div>
          </>
        )}

        {brand && brand.description && (
          <section className="bg-white rounded-xl shadow-sm p-6 sm:p-10 mt-8" data-testid="brand-description">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{brand.main_keyword ? `${brand.main_keyword} : tout savoir` : `À propos de ${brand.name}`}</h2>
            <div className="prose-legal text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMarkdown(brand.description) }} />
          </section>
        )}

        {brand && <FaqSection items={brand.faq} title={`Questions fréquentes sur ${brand.main_keyword || brand.name}`} testId="brand-faq" />}

        {brand && lang === 'fr' && <TagLinks tags={brand.tags} title={t('brand.relatedSearches')} testIdPrefix="brand-tag" sectionTestId="brand-tags" />}
      </main>
      <Footer />
    </div>
  );
};

export default BrandPage;
